import { Router } from 'express';
import { ChatController } from '../controllers/chat.controller';
import { authMiddleware } from 'middlewares/authMiddleware';
import { permissionInjector } from 'middlewares/permissionInjector';
import { UploadMultipleFiles } from 'middlewares/storage';

export class ChatRouter {
    public router: Router;
    public path = '/chat';
    public chatController = new ChatController();

    constructor() {
        this.router = Router();
        this.routes();
    }

    private routes() {
        /**
         * @swagger
         * /chat:
         *   post:
         *     summary: Create a new chat
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               participants:
         *                 type: array
         *                 items:
         *                   type: string
         *                 description: List of participant user IDs (exactly 2)
         *                 example:
         *                  ["507f1f77bcf86cd799439011", "507f1f77bcf86cd799439011"]
         *     responses:
         *       201:
         *         description: Chat created successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Chat created successfully
         *                 data:
         *                   type: object
         *                   properties:
         *                     _id:
         *                       type: string
         *                     participants:
         *                       type: array
         *                       items:
         *                         type: string
         *                     createdBy:
         *                       type: string
         *                     created_at:
         *                       type: string
         *                       format: date-time
         *       400:
         *         description: Invalid request (e.g., incorrect number of participants)
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden (e.g., labour-to-labour chat)
         *       500:
         *         description: Internal server error
         */
        this.router.post(
            `${this.path}`,
            authMiddleware,
            permissionInjector(this.path, 'POST'),
            this.chatController.createChat
        );

        /**
         * @swagger
         * /chat/message:
         *   post:
         *     summary: Send a message in a chat
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     requestBody:
         *       required: true
         *       content:
         *         application/json:
         *           schema:
         *             type: object
         *             properties:
         *               chatId:
         *                 type: string
         *               content:
         *                 type: string
         *                 nullable: true
         *               attachments:
         *                 type: array
         *                 items:
         *                   type: string
         *               receiverId:
         *                 type: string

         *           example:
         *             chatId: "507f1f77bcf86cd799439011"
         *             content: "Hello, how are you?"
         *             attachments: ["507f1f77bcf86cd799439011"]
         *             receiverId: "507f1f77bcf86cd799439011"
         *     responses:
         *       201:
         *         description: Message sent successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Message created successfully
         *                 data:
         *                   $ref: '#/components/schemas/Message'
         *       400:
         *         description: Invalid request (e.g., invalid chatId or attachments)
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: Chat or user not found
         *       500:
         *         description: Internal server error
         */
        this.router.post(
            `${this.path}/message`,
            authMiddleware,
            permissionInjector(`${this.path}/message`, 'POST'),
            this.chatController.sendMessage
        );

        /**
         * @swagger
         * /chat/{chatId}:
         *   get:
         *     summary: Get chat history
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: chatId
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the chat to retrieve history for
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 20
         *         description: Number of messages to return per page
         *     responses:
         *       200:
         *         description: Chat history retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Successfully fetched Messages
         *                 data:
         *                   type: object
         *                   properties:
         *                     data:
         *                       type: array
         *                       items:
         *                         $ref: '#/components/schemas/Message'
         *                     total:
         *                       type: integer
         *                     page:
         *                       type: integer
         *                     totalPage:
         *                       type: integer
         *                     participantStatuses:
         *                       type: object
         *                       additionalProperties:
         *                         type: string
         *                         enum: [online, offline]
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: Chat not found
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/:chatId`,
            authMiddleware,
            permissionInjector(this.path, 'GET'),
            this.chatController.getChatHistory
        );

        /**
         * @swagger
         * /chat/message/{messageId}/read:
         *   post:
         *     summary: Mark a message as read
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: messageId
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the message to mark as read
         *     responses:
         *       200:
         *         description: Message marked as read
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Message updated successfully
         *                 data:
         *                   type: object
         *                   properties:
         *                     messageId:
         *                       type: string
         *                     chatId:
         *                       type: string
         *                     isRead:
         *                       type: boolean
         *                     updated_at:
         *                       type: string
         *                       format: date-time
         *       400:
         *         description: Invalid messageId
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: Message not found
         *       500:
         *         description: Internal server error
         */
        this.router.post(
            `${this.path}/message/:messageId/read`,
            authMiddleware,
            permissionInjector(`${this.path}/message/read`, 'POST'),
            this.chatController.markMessageAsRead
        );

        /**
         * @swagger
         * /chat/detail/{chatId}:
         *   get:
         *     summary: Get chat details by ID
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: chatId
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the chat to retrieve
         *     responses:
         *       200:
         *         description: Chat details retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Successfully fetched Chat
         *                 data:
         *                   $ref: '#/components/schemas/Chat'
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: Chat not found
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/detail/:chatId`,
            authMiddleware,
            permissionInjector(`${this.path}/detail`, 'GET'),
            this.chatController.getById
        );

        /**
         * @swagger
         * /chat:
         *   get:
         *     summary: Get all chats for the authenticated user
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 1
         *         description: Page number for pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 20
         *         description: Number of chats to return per page
         *     responses:
         *       200:
         *         description: List of chats retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Successfully fetched Chats
         *                 data:
         *                   type: object
         *                   properties:
         *                     data:
         *                       type: array
         *                       items:
         *                         $ref: '#/components/schemas/Chat'
         *                     total:
         *                       type: integer
         *                     page:
         *                       type: integer
         *                     totalPage:
         *                       type: integer
         *       401:
         *         description: Unauthorized
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}`,
            authMiddleware,
            permissionInjector(this.path, 'GET'),
            this.chatController.getAllChats
        );

        /**
         * @swagger
         * /chat/user/{userId}:
         *   get:
         *     summary: Get all chats and messages with a specific user
         *     tags: [Chat]
         *     security:
         *       - bearerAuth: []
         *     parameters:
         *       - in: path
         *         name: userId
         *         required: true
         *         schema:
         *           type: string
         *         description: ID of the user to retrieve chats and messages with
         *       - in: query
         *         name: page
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 1
         *         description: Page number for message pagination
         *       - in: query
         *         name: limit
         *         schema:
         *           type: integer
         *           minimum: 1
         *           default: 20
         *         description: Number of messages to return per page
         *     responses:
         *       200:
         *         description: Chats and messages retrieved successfully
         *         content:
         *           application/json:
         *             schema:
         *               type: object
         *               properties:
         *                 status:
         *                   type: string
         *                   example: OK
         *                 message:
         *                   type: string
         *                   example: Successfully fetched chats and messages
         *                 data:
         *                   type: object
         *                   properties:
         *                     chats:
         *                       type: array
         *                       items:
         *                         $ref: '#/components/schemas/ChatWithMessages'
         *                     total:
         *                       type: integer
         *                     page:
         *                       type: integer
         *                     totalPage:
         *                       type: integer
         *       401:
         *         description: Unauthorized
         *       403:
         *         description: Forbidden
         *       404:
         *         description: User or chat not found
         *       500:
         *         description: Internal server error
         */
        this.router.get(
            `${this.path}/user/:userId`,
            authMiddleware,
            permissionInjector(`${this.path}/user`, 'GET'),
            this.chatController.getChatsWithUser
        );
    }
}

/**
 * @swagger
 * components:
 *   schemas:
 *     User:
 *       type: object
 *       properties:
 *         userId:
 *           type: string
 *         firstName:
 *           type: string
 *         lastName:
 *           type: string
 *         email:
 *           type: string
 *           format: email
 *         profileImage:
 *           type: object
 *           nullable: true
 *           properties:
 *             _id:
 *               type: string
 *             filename:
 *               type: string
 *             url:
 *               type: string
 *               format: uri
 *             thumbnailUrl:
 *               type: string
 *               format: uri
 *               nullable: true
 *     Attachment:
 *       type: object
 *       properties:
 *         attachmentId:
 *           type: string
 *         filename:
 *           type: string
 *         url:
 *           type: string
 *           format: uri
 *         mimetype:
 *           type: string
 *         size:
 *           type: integer
 *         thumbnailUrl:
 *           type: string
 *           format: uri
 *           nullable: true
 *     Message:
 *       type: object
 *       properties:
 *         messageId:
 *           type: string
 *         chatId:
 *           type: string
 *         sender:
 *           $ref: '#/components/schemas/User'
 *         receiver:
 *           $ref: '#/components/schemas/User'
 *         content:
 *           type: string
 *           nullable: true
 *         attachments:
 *           type: array
 *           items:
 *             $ref: '#/components/schemas/Attachment'
 *         isRead:
 *           type: boolean
 *         created_at:
 *           type: string
 *           format: date-time
 *     Chat:
 *       type: object
 *       properties:
 *         chatId:
 *           type: string
 *         participants:
 *           type: array
 *           items:
 *             $ref: '#/components/schemas/User'
 *         createdBy:
 *           type: string

 *         created_at:
 *           type: string
 *           format: date-time
 *         participantStatuses:
 *           type: object
 *           additionalProperties:
 *             type: string
 *             enum: [online, offline]
 *     ChatWithMessages:
 *       allOf:
 *         - $ref: '#/components/schemas/Chat'
 *         - type: object
 *           properties:
 *             messages:
 *               type: array
 *               items:
 *                 $ref: '#/components/schemas/Message'
 *   securitySchemes:
 *     bearerAuth:
 *       type: http
 *       scheme: bearer
 *       bearerFormat: JWT
 */